import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Star, Film, Camera, Loader } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, API } from "@/lib/api";

const thumbUrl = (id) => `${API}/media/${id}/thumbnail`;

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all|video|photo
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({ kind: "video", title: "", description: "", featured: false });
  const fileRef = useRef(null);

  const load = () => {
    api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a file first.");
    if (!form.title.trim()) return toast.error("Title required.");

    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", form.kind);
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("featured", form.featured ? "true" : "false");

    setUploading(true);
    setProgress(0);
    try {
      await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          if (p.total) setProgress(Math.round((p.loaded / p.total) * 100));
        },
      });
      toast.success("Upload complete.");
      setForm({ kind: form.kind, title: "", description: "", featured: false });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this media? It will hide from the public site.")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await api.patch(`/media/${item.id}`, { featured: !item.featured });
      load();
    } catch {
      toast.error("Failed.");
    }
  };

  const filtered = items.filter((i) => filter === "all" || i.kind === filter);

  return (
    <div className="p-8 lg:p-12" data-testid="admin-media-page">
      <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-2">MEDIA</div>
      <h1 className="font-bodoni text-5xl md:text-7xl mb-12">Archive.</h1>

      {/* Upload */}
      <div className="border border-neutral-900 p-8 mb-12" data-testid="upload-section">
        <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-6">NEW TRANSMISSION</div>
        <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-2">KIND</div>
            <div className="grid grid-cols-2 gap-2">
              {["video", "photo"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setForm({ ...form, kind: k })}
                  data-testid={`upload-kind-${k}`}
                  className={`px-3 py-2 border font-mono text-xs ${
                    form.kind === k ? "border-white bg-white text-black" : "border-neutral-800 text-neutral-400"
                  }`}
                >
                  {k.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-2">TITLE</div>
            <input data-testid="upload-title" className="noir-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-2">FILE</div>
            <input
              ref={fileRef}
              type="file"
              data-testid="upload-file"
              accept={form.kind === "video" ? "video/*" : "image/*"}
              className="noir-input file:bg-white file:text-black file:border-0 file:px-3 file:py-1 file:font-mono file:text-xs file:mr-3"
            />
          </div>
          <div className="md:col-span-12">
            <div className="font-mono text-[10px] text-neutral-500 tracking-widest mb-2">DESCRIPTION</div>
            <textarea data-testid="upload-description" rows={2} className="noir-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="md:col-span-12 flex items-center justify-between flex-wrap gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="upload-featured"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="accent-white"
              />
              <span className="font-mono text-xs text-neutral-400 tracking-widest">FEATURE ON HOMEPAGE</span>
            </label>
            <button type="submit" disabled={uploading} data-testid="upload-submit" className="btn-noir">
              {uploading ? (<><Loader size={14} className="animate-spin" /> UPLOADING {progress}%</>) : (<><Upload size={14} /> Upload</>)}
            </button>
          </div>
          {uploading && (
            <div className="md:col-span-12">
              <div className="h-1 bg-neutral-900">
                <div className="h-full bg-white transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {["all", "video", "photo"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 font-mono text-[10px] tracking-widest border ${
                filter === f ? "border-white bg-white text-black" : "border-neutral-900 text-neutral-400"
              }`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="font-mono text-[10px] text-neutral-500 tracking-widest">
          {filtered.length} ITEMS
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-neutral-900 p-16 text-center font-mono text-xs text-neutral-600 tracking-widest">
          NO MEDIA YET
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <div key={m.id} className="border border-neutral-900 bg-neutral-950 group" data-testid={`media-row-${m.id}`}>
              <div className="aspect-video bg-black overflow-hidden relative">
                {m.kind === "video" ? (
                  <video src={fileUrl(m.id)} poster={m.thumbnail_path ? thumbUrl(m.id) : undefined} preload="metadata" muted loop className="w-full h-full object-cover grayscale" onMouseEnter={(e) => e.currentTarget.play()} onMouseLeave={(e) => e.currentTarget.pause()} />
                ) : (
                  <img src={fileUrl(m.id)} alt={m.title} className="w-full h-full object-cover grayscale" loading="lazy" />
                )}
                <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/70 px-2 py-1 font-mono text-[9px] tracking-widest">
                  {m.kind === "video" ? <Film size={10} /> : <Camera size={10} />} {m.kind.toUpperCase()}
                </div>
                {m.featured && (
                  <div className="absolute top-2 right-2 bg-white text-black px-2 py-1 font-mono text-[9px] tracking-widest">FEATURED</div>
                )}
              </div>
              <div className="p-4">
                <div className="font-bodoni text-lg mb-1 line-clamp-1">{m.title}</div>
                {m.description && <div className="font-mono text-[10px] text-neutral-500 line-clamp-2 mb-3">{m.description}</div>}
                <div className="flex items-center justify-between mt-3">
                  <button onClick={() => toggleFeatured(m)} data-testid={`feat-${m.id}`} className={`flex items-center gap-1 font-mono text-[10px] tracking-widest ${m.featured ? "text-white" : "text-neutral-500 hover:text-white"}`}>
                    <Star size={12} fill={m.featured ? "currentColor" : "none"} /> {m.featured ? "UNFEATURE" : "FEATURE"}
                  </button>
                  <button onClick={() => remove(m.id)} data-testid={`del-${m.id}`} className="flex items-center gap-1 font-mono text-[10px] tracking-widest text-neutral-500 hover:text-white">
                    <Trash2 size={12} /> DELETE
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
